import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const PLATFORMS = [
  { key: 'eleven', label: '11번가' },
  { key: 'naver', label: '스마트스토어' },
  { key: 'esm', label: 'ESM(지마켓/옥션)' },
  { key: 'sellerhub', label: '셀러허브' },
  { key: 'coupang', label: '쿠팡' },
  { key: 'toss', label: '토스쇼핑' },
];

export default function CategoryNotification() {
  const [platform, setPlatform] = useState('eleven');
  const [rows, setRows] = useState([]);
  const [noticeTypes, setNoticeTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [expanded, setExpanded] = useState({});

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        api.get('/categories/notice-mappings', { params: { platform } }),
        api.get('/categories/notice-mappings/types', { params: { platform } }),
      ]);
      setRows(r1.data || []);
      setNoticeTypes(r2.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [platform]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredRows = useMemo(() => rows.filter(r => {
    const ourPath = [r.large_category, r.middle_category, r.small_category, r.detail_category].filter(Boolean).join(' > ');
    if (search && !ourPath.includes(search) && !(r.platform_category_name || '').includes(search)) return false;
    if (filter === 'matched' && !r.notice_type) return false;
    if (filter === 'unmatched' && r.notice_type) return false;
    return true;
  }), [rows, search, filter]);

  const tree = useMemo(() => {
    const t = {};
    filteredRows.forEach(r => {
      const large = r.large_category || '(미분류)';
      const middle = r.middle_category || '(미분류)';
      if (!t[large]) t[large] = {};
      if (!t[large][middle]) t[large][middle] = [];
      t[large][middle].push(r);
    });
    return t;
  }, [filteredRows]);

  useEffect(() => {
    if (!search) return;
    const next = {};
    Object.entries(tree).forEach(([large, middles]) => {
      next[large] = true;
      Object.keys(middles).forEach(middle => { next[`${large}>${middle}`] = true; });
    });
    setExpanded(prev => ({ ...prev, ...next }));
  }, [search, tree]);

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const matchedCount = rows.filter(r => r.notice_type).length;
  const unmatchedCount = rows.length - matchedCount;

  const selectType = async (row, noticeType) => {
    try {
      await api.post('/categories/notice-mappings', { category_id: row.id, platform, notice_type: noticeType });
      setEditingId(null);
      fetchAll();
    } catch (err) { console.error(err); alert('저장 실패'); }
  };

  const groupProgress = (items) => {
    const matched = items.filter(i => i.notice_type).length;
    return `${matched}/${items.length}`;
  };

  return (
    <div style={{ padding: '24px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '500', marginBottom: '16px' }}>고시정보 관리</h2>
      <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '16px' }}>
        각 판매처 카테고리에 맞는 상품정보제공고시 유형을 매칭합니다. 실제 항목 값은 상품 전송 시 자동으로 채워집니다.<br/>
        쿠팡은 카테고리마다 선택 가능한 고시정보 유형이 다릅니다(오픈마켓 매칭 시 API로 미리 확보해둔 유형 중에서 고릅니다).
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        {PLATFORMS.map(p => (
          <button key={p.key} onClick={() => setPlatform(p.key)}
            style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
              border: platform === p.key ? '1px solid #378ADD' : '1px solid #ddd',
              color: platform === p.key ? '#185FA5' : '#888', background: '#fff', fontWeight: platform === p.key ? '500' : '400' }}
          >{p.label}</button>
        ))}
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: '13px', color: '#888' }}>매칭완료 {matchedCount} · 미매칭 {unmatchedCount}</span>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <input type="text" placeholder="카테고리명으로 검색" value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }} />
        <select value={filter} onChange={e => setFilter(e.target.value)} style={{ width: '140px', padding: '8px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}>
          <option value="all">전체</option>
          <option value="unmatched">미매칭만</option>
          <option value="matched">매칭완료만</option>
        </select>
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>불러오는 중...</div>
      ) : (
        <div style={{ border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden' }}>
          {Object.entries(tree).map(([large, middles]) => {
            const largeItems = Object.values(middles).flat();
            const largeOpen = !!expanded[large];
            return (
              <div key={large}>
                <div onClick={() => toggle(large)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: '#fafafa', cursor: 'pointer', borderTop: '1px solid #eee' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '500' }}>
                    <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: largeOpen ? 'rotate(90deg)' : 'none' }}>›</span>
                    {large}
                  </div>
                  <span style={{ fontSize: '12px', color: '#888' }}>{groupProgress(largeItems)} 매칭</span>
                </div>
                {largeOpen && Object.entries(middles).map(([middle, items]) => {
                  const middleKey = `${large}>${middle}`;
                  const middleOpen = !!expanded[middleKey];
                  return (
                    <div key={middle} style={{ paddingLeft: '20px' }}>
                      <div onClick={() => toggle(middleKey)}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px 10px 0', cursor: 'pointer', borderTop: '1px solid #f2f2f2' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#555' }}>
                          <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: middleOpen ? 'rotate(90deg)' : 'none' }}>›</span>
                          {middle}
                        </div>
                        <span style={{ fontSize: '11px', color: '#aaa' }}>{groupProgress(items)}</span>
                      </div>
                      {middleOpen && (
                        <div style={{ paddingLeft: '20px' }}>
                          {items.map(row => {
                            const ourName = row.detail_category || row.small_category;
                            return (
                              <div key={row.id}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.2fr 100px', alignItems: 'center', padding: '8px 14px 8px 0', borderTop: '1px solid #f7f7f7', fontSize: '13px' }}>
                                  <div>{ourName}</div>
                                  <div style={{ color: '#999', fontSize: '12px' }}>{row.platform_category_name || '-'}</div>
                                  <div>
                                    {row.notice_type ? (
                                      <span>{row.notice_type_name} <span style={{ color: '#bbb', fontSize: '11px' }}>({row.notice_type})</span></span>
                                    ) : (
                                      <span style={{ color: '#aaa' }}>미매칭</span>
                                    )}
                                  </div>
                                  <button onClick={() => setEditingId(editingId === row.id ? null : row.id)} style={{ fontSize: '12px', padding: '4px 10px', justifySelf: 'end', borderRadius: '6px', border: row.notice_type ? '1px solid #ddd' : '1px solid #378ADD', color: row.notice_type ? '#555' : '#185FA5', background: '#fff', cursor: 'pointer' }}>
                                    {row.notice_type ? '수정' : '매칭'}
                                  </button>
                                </div>
                                {editingId === row.id && (
                                  <div style={{ padding: '10px 14px', background: '#fafafa', borderTop: '1px solid #f0f0f0', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {/* [설계 원칙 - 2026-09-09] 쿠팡은 카테고리마다 선택 가능한 고시유형이 달라서
                                        (row.available_notice_types), 다른 판매처처럼 전역 noticeTypes를 쓰지 않는다.
                                        다른 판매처(naver/eleven/esm/sellerhub) 렌더링은 그대로 유지한다. */}
                                    {(platform === 'coupang' ? (row.available_notice_types || []) : noticeTypes).map(nt => (
                                      <button key={nt.code} onClick={() => selectType(row, nt.code)}
                                        style={{ fontSize: '12px', padding: '6px 12px', borderRadius: '6px', border: row.notice_type === nt.code ? '1px solid #378ADD' : '1px solid #ddd', color: row.notice_type === nt.code ? '#185FA5' : '#555', background: '#fff', cursor: 'pointer' }}
                                      >{nt.name}{nt.itemCount != null ? <span style={{ color: '#bbb' }}> ({nt.itemCount}개 항목)</span> : null}</button>
                                    ))}
                                    {platform === 'coupang' && (row.available_notice_types || []).length === 0 && (
                                      <span style={{ fontSize: '12px', color: '#aaa' }}>이 카테고리에 대한 쿠팡 고시정보 유형을 찾을 수 없습니다. 오픈마켓 매칭을 먼저 확인해주세요.</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
